import {
  DEFAULT_BEWERKINGSTARIEVEN,
  type BewerkingsTarieven,
  EMPTY_OVERRIDES,
  PricingError,
  bulkUnitCents,
  calculateGlassPrice,
  catalogFactor,
  effectivePricePerM2,
  emailInstelkostBiCents,
  emailInstelkostCents,
  grindFamilyForSlug,
  grindKeyFor,
  isEmailVariantColor,
  type CatalogOverrides,
  type Cents,
} from "@glastotaal/core";
import { getArticle, getVariant } from "@/lib/catalog";
import { priceWithSale, type PromoMap } from "@/lib/promotions";
import type { BulkIndex } from "@/lib/article-bulk";
import { buildEdgeFinish, edgeFinishOptions } from "@/features/configurator/edge-options";
import { SPELING_MM } from "@/features/configurator/constants";
import type { CartItem } from "./types";

/** Minimal price the cart needs (line total excl. BTW); cart never stores amounts. */
export interface CartLinePrice {
  subtotalExcl: Cents;
}

/**
 * Recomputes a cart line's total from the catalog for DISPLAY only — the server
 * recomputes the authoritative price at checkout. Returns null when a glass line's
 * variant/pricing is no longer valid.
 *
 * `overrides` = de live admin-prijslaag. Die MOET mee: zonder de overrides rekent de
 * winkelwagen met de kale catalogusprijs, terwijl de configurator en de server allebei de
 * ingestelde marge/prijs gebruiken — dan verspringt het bedrag tussen configurator, wagen en
 * bestelling. Standaard leeg zodat een aanroep zonder overrides niet stukloopt.
 */
export function cartItemPrice(
  item: CartItem,
  overrides: CatalogOverrides = EMPTY_OVERRIDES,
  promos: PromoMap = {},
  bulk: BulkIndex = {},
  /** Inkooptarieven bewerkingen uit de admin; weglaten = de code-standaard. De server rekent met
   *  dezelfde tarieven, dus laten we ze hier weg dan wijkt de getoonde prijs af van de afrekening. */
  tarieven: BewerkingsTarieven = DEFAULT_BEWERKINGSTARIEVEN,
): CartLinePrice | null {
  // Montage-regel (laten plaatsen via ProGlass): prijs vervalt volledig — op aanvraag, telt niet
  // mee in het winkelwagen-totaal (ProGlass hanteert eigen tarieven). Geldt voor glas op maat én
  // configureerbare artikelen (ventilatieroosters).
  if ((item.kind === "glass" || item.kind === "isolatieglas" || item.kind === "benglas" || item.kind === "article") && item.montage) return null;

  if (item.kind === "article") {
    // Gewoon catalogusartikel: prijs opnieuw uit de catalogus halen in plaats van de momentopname
    // van het toevoegen te vertrouwen. Verandert de admin-marge of komt er een actie bij, dan
    // klopt de winkelwagen meteen — en een regel die ooit met een verkeerd bedrag is toegevoegd,
    // herstelt zichzelf. Alles wat een eigen berekening heeft (rooster, op lengte, voorzetraam,
    // variant, prijs-op-aanvraag) houdt de opgeslagen prijs; die valt hier niet na te rekenen.
    const eigenBerekening =
      item.onRequest || !!item.roosterConfig || item.lengthMm != null || item.wMm != null || item.hMm != null;
    const art = eigenBerekening ? undefined : getArticle(item.articleSlug);
    let unit = item.priceCents;
    if (art) {
      const verkoop = priceWithSale(
        effectivePricePerM2(art.priceCents, art.slug, overrides, [art.category]),
        promos[art.slug],
      ).priceCents;
      // Staffelkorting volgt het aantal dat er nú in de wagen ligt — dezelfde helper als de
      // productpagina en de server. Zonder dit bleef de wagen op de stukprijs van het moment van
      // toevoegen staan en betaalde de klant bij het afrekenen ineens minder.
      unit = bulkUnitCents(verkoop, art.priceCents, bulk[art.slug], item.quantity);
    }
    const base = unit * item.quantity;
    // onceKey-toeslagen (bv. speciale-RAL-instelkost) tellen NIET in de regelprijs:
    // die rekenen we 1× per groep over de hele winkelwagen (zie cartOnceExtras).
    const extra = (item.surcharges ?? []).reduce(
      (sum, s) => sum + (s.onceKey ? 0 : s.once ? s.amountCents : s.amountCents * item.quantity),
      0,
    );
    return { subtotalExcl: base + extra };
  }

  if (item.kind === "benglas" || item.kind === "isolatieglas" || item.kind === "shower") {
    return { subtotalExcl: item.priceCents };
  }

  const variant = getVariant(item.productSlug, item.variantId);
  if (!variant) return null;

  const { config } = item;
  const shape =
    config.shapeId && config.shapeId !== "rechthoek"
      ? variant.vormen?.find((v) => v.id === config.shapeId)
      : undefined;

  // Vorm meegeven: op een model kost slijpen 100% meer, en dat zit in het m1-tarief van de
  // optie. Zonder de vorm zou de winkelwagen een lager slijptarief pakken dan de configurator.
  const finishOpt = config.edgeFinishId
    ? edgeFinishOptions(variant, config.shapeId, (config.widthMm / 1000) * (config.heightMm / 1000)).find((o) => o.id === config.edgeFinishId)
    : undefined;
  const edgeFinish = finishOpt ? buildEdgeFinish(finishOpt) : undefined;

  // Prijs-context exact zoals de configurator en de server 'm opbouwen: de admin-prijslaag over
  // de €/m², de slijptabel van de juiste glasfamilie, en dezelfde marge op de bewerkingen.
  // Ontbrak dit, dan pakte de winkelwagen voor gelaagd glas de float-slijptabel.
  const scopes = [variant.subcategory ?? "", item.productSlug];
  const effM2 = effectivePricePerM2(variant.pricePerM2, variant.id, overrides, scopes);
  const priced = effM2 === variant.pricePerM2 ? variant : { ...variant, pricePerM2: effM2 as Cents };
  const family = grindFamilyForSlug(variant.subcategory ?? "");

  try {
    return calculateGlassPrice(config, {
      variant: priced,
      shape,
      edgeFinish,
      spelingMm: SPELING_MM,
      grindLengthMm: item.grindLengthMm,
      grindFamily: family,
      grindKey: grindKeyFor(family, variant),
      opsMarginFactor: catalogFactor(overrides, scopes, variant.id),
      opsTarieven: tarieven,
      productSlug: item.productSlug,
    });
  } catch (err) {
    if (err instanceof PricingError) return null;
    throw err;
  }
}

/**
 * Eenmalige kosten die over de héle winkelwagen maar één keer gelden, gegroepeerd op
 * `onceKey` (bv. de speciale-RAL-instelkost per kleur). Eerste voorkomen wint qua label,
 * het hoogste bedrag wint. Wordt los van de regels bij het subtotaal opgeteld én getoond.
 */
export function cartOnceExtras(items: CartItem[]): { key: string; label: string; amountCents: Cents }[] {
  const byKey = new Map<string, { label: string; amountCents: Cents }>();
  const bump = (key: string, label: string, amountCents: Cents): void => {
    const cur = byKey.get(key);
    if (cur) cur.amountCents = Math.max(cur.amountCents, amountCents);
    else byKey.set(key, { label, amountCents });
  };
  for (const item of items) {
    // Artikel-toeslagen met onceKey (bv. speciale-RAL-instelkost rooster).
    if (item.kind === "article" && item.surcharges) {
      for (const s of item.surcharges) if (s.onceKey) bump(s.onceKey, s.label, s.amountCents);
    }
    // Geëmailleerd glas: eenmalige instelkost (€125/€135) 1× per (kleur)keuze per order.
    // BiColour → de duurste van de twee kleuren telt (1× instelkost, niet 2×).
    if (item.kind === "glass" && item.config.ral) {
      const variant = getVariant(item.productSlug, item.config.variantId);
      if (variant && isEmailVariantColor(variant.color)) {
        const code = item.config.ral.trim();
        const codeBinnen = item.config.ralBinnen?.trim();
        if (codeBinnen) {
          const key = `email-ral:${[code, codeBinnen].map((c) => c.replace(/\D/g, "")).sort().join("+")}`;
          bump(key, `Instelkost geëmailleerd glas (RAL ${code} buiten · ${codeBinnen} binnen)`, emailInstelkostBiCents(code, codeBinnen));
        } else {
          bump(`email-ral:${code.replace(/\D/g, "")}`, `Instelkost geëmailleerd glas (RAL ${code})`, emailInstelkostCents(code));
        }
      }
    }
  }
  return [...byKey.entries()].map(([key, v]) => ({ key, ...v }));
}
